import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Loader2, ListChecks } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/app/components/ui/alert-dialog';
import {
  useOnboardingChecklists,
  useCreateOnboardingChecklist,
  useDeleteOnboardingChecklist,
  useAddOnboardingItem,
  useUpdateOnboardingItem,
} from '@/hooks/use-crew';
import { usePermissions } from '@/hooks/use-permissions';
import { handleApiError } from '@/lib/api-client';
import type { OnboardingItemType, TechCertType } from '@/lib/types';

const itemTypeLabels: Record<OnboardingItemType, string> = {
  upload_document: 'Upload Document',
  complete_training: 'Complete Training',
  sign_form: 'Sign Form',
  add_certification: 'Add Certification',
  custom: 'Custom',
};

const certTypeLabels: Record<TechCertType, string> = {
  sprat_1: 'SPRAT Level 1',
  sprat_2: 'SPRAT Level 2',
  sprat_3: 'SPRAT Level 3',
  irata_1: 'IRATA Level 1',
  irata_2: 'IRATA Level 2',
  irata_3: 'IRATA Level 3',
  first_aid: 'First Aid',
  cpr: 'CPR',
  confined_space: 'Confined Space',
  fall_protection: 'Fall Protection',
  rigging: 'Rigging',
  crane_signal: 'Crane Signal',
  hazmat: 'HAZMAT',
  osha_10: 'OSHA 10',
  osha_30: 'OSHA 30',
  twic: 'TWIC',
  custom: 'Custom',
};

export function OnboardingAdminPage() {
  const { canManageOnboarding } = usePermissions();
  const { data: checklists, isLoading } = useOnboardingChecklists();
  const createMutation = useCreateOnboardingChecklist();
  const deleteMutation = useDeleteOnboardingChecklist();
  const addItemMutation = useAddOnboardingItem();
  const updateItemMutation = useUpdateOnboardingItem();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addItemOpen, setAddItemOpen] = useState<number | null>(null);
  const [deleteChecklistId, setDeleteChecklistId] = useState<number | null>(null);
  const [itemForm, setItemForm] = useState({
    title: '',
    description: '',
    item_type: 'custom' as OnboardingItemType,
    is_required: true,
    sort_order: 0,
    required_cert_type: '' as TechCertType | '',
  });

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    try {
      await createMutation.mutateAsync({ name, is_default: isDefault });
      setCreateOpen(false);
      setName('');
      setIsDefault(false);
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  const handleAddItem = async (checklistId: number) => {
    setError(null);
    if (!itemForm.title.trim()) {
      setError('Item title is required.');
      return;
    }
    try {
      await addItemMutation.mutateAsync({ checklistId, data: itemForm });
      setAddItemOpen(null);
      setItemForm({
        title: '',
        description: '',
        item_type: 'custom',
        is_required: true,
        sort_order: 0,
        required_cert_type: '',
      });
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/crew">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Onboarding Checklists</h1>
          <p className="text-gray-600">Manage onboarding templates for new technicians</p>
        </div>
        {canManageOnboarding && (
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-1" />
                New Checklist
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Onboarding Checklist</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}
              <div>
                <Label>Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. New Tech Onboarding" />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="is_default">Auto-assign to new techs</Label>
              </div>
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Create
              </Button>
            </div>
          </DialogContent>
          </Dialog>
        )}
      </div>

      {(!checklists || checklists.results.length === 0) ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ListChecks className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No onboarding checklists yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {checklists.results.map((checklist) => (
            <Card key={checklist.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle className="text-lg">{checklist.name}</CardTitle>
                  {checklist.is_default && (
                    <Badge className="mt-1 bg-blue-100 text-blue-800">Default</Badge>
                  )}
                </div>
                {canManageOnboarding && (
                  <div className="flex items-center gap-2" data-testid="checklist-actions">
                    <Dialog
                      open={addItemOpen === checklist.id}
                      onOpenChange={(open) => setAddItemOpen(open ? checklist.id : null)}
                    >
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline">
                        <Plus className="h-3 w-3 mr-1" />
                        Add Item
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Onboarding Item</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}
                        <div>
                          <Label>Title</Label>
                          <Input
                            value={itemForm.title}
                            onChange={(e) => setItemForm((f) => ({ ...f, title: e.target.value }))}
                          />
                        </div>
                        {itemForm.item_type === 'add_certification' && (
                          <div>
                            <Label>Matching certification</Label>
                            <Select
                              value={itemForm.required_cert_type}
                              onValueChange={(value) => setItemForm((form) => ({
                                ...form,
                                required_cert_type: value as TechCertType,
                              }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Select certification" /></SelectTrigger>
                              <SelectContent>
                                {Object.entries(certTypeLabels).map(([value, label]) => (
                                  <SelectItem key={value} value={value}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label>Description</Label>
                          <Input
                            value={itemForm.description}
                            onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Type</Label>
                          <Select
                            value={itemForm.item_type}
                            onValueChange={(v) => setItemForm((f) => ({ ...f, item_type: v as OnboardingItemType }))}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(itemTypeLabels).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id="item_required"
                            checked={itemForm.is_required}
                            onChange={(e) => setItemForm((f) => ({ ...f, is_required: e.target.checked }))}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <Label htmlFor="item_required">Required</Label>
                        </div>
                        <Button
                          onClick={() => handleAddItem(checklist.id)}
                          disabled={addItemMutation.isPending}
                          className="w-full"
                        >
                          {addItemMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                          Add Item
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                    <AlertDialog
                      open={deleteChecklistId === checklist.id}
                      onOpenChange={(open) => setDeleteChecklistId(open ? checklist.id : null)}
                    >
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          aria-label="Delete checklist"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Checklist</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{checklist.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              deleteMutation.mutate(checklist.id, {
                                onSuccess: () => setDeleteChecklistId(null),
                              });
                            }}
                            disabled={deleteMutation.isPending}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {checklist.items.length === 0 ? (
                  <p className="text-sm text-gray-500">No items yet. Add items to build the checklist.</p>
                ) : (
                  <div className="space-y-2">
                    {checklist.items.map((item, idx) => (
                      <div key={item.id} className="flex items-center gap-3 p-2 rounded bg-gray-50">
                        <span className="text-sm font-medium text-gray-400 w-6">{idx + 1}</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-gray-500">{item.description}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {itemTypeLabels[item.item_type]}
                        </Badge>
                        {item.item_type === 'add_certification' && (
                          canManageOnboarding ? (
                            <Select
                              value={item.required_cert_type || undefined}
                              onValueChange={(value) => updateItemMutation.mutate({
                                checklistId: checklist.id,
                                itemId: item.id,
                                data: { required_cert_type: value as TechCertType },
                              })}
                              disabled={updateItemMutation.isPending}
                            >
                              <SelectTrigger className="h-8 w-44 text-xs">
                                <SelectValue placeholder="Select certification" />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(certTypeLabels).map(([value, label]) => (
                                  <SelectItem key={value} value={value}>{label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : item.required_cert_type ? (
                            <Badge variant="outline" className="text-xs">
                              {certTypeLabels[item.required_cert_type]}
                            </Badge>
                          ) : null
                        )}
                        {canManageOnboarding ? (
                          <label className="flex items-center gap-1 text-xs">
                            <input
                              type="checkbox"
                              checked={item.is_required}
                              disabled={updateItemMutation.isPending}
                              onChange={(event) => updateItemMutation.mutate({
                                checklistId: checklist.id,
                                itemId: item.id,
                                data: { is_required: event.target.checked },
                              })}
                            />
                            Required
                          </label>
                        ) : item.is_required ? (
                          <Badge className="bg-red-50 text-red-700 text-xs">Required</Badge>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
